/* ============================================================
   useDatabase — 데이터베이스 페이지의 스키마/뷰/행 로드·생성·수정 훅
   - workspace 스키마(supabase.schema('workspace')) 직접 호출
   - 스키마/뷰가 없으면 앱에서 기본값 insert (DB 마이그레이션 불필요)
   ============================================================ */
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../contexts/AuthContext';

const ws = () => supabase.schema('workspace');

/* 기본 컬럼: 이름(title) / 상태(select) / 담당자(person) / 마감일(date) */
export const DEFAULT_COLUMNS = [
  { key: 'title', name: '이름', type: 'title' },
  {
    key: 'status',
    name: '상태',
    type: 'select',
    options: [
      { name: '대기', color: 'gray' },
      { name: '진행중', color: 'blue' },
      { name: '완료', color: 'green' },
    ],
  },
  { key: 'assignee', name: '담당자', type: 'person' },
  { key: 'due', name: '마감일', type: 'date' },
];

export function useDatabase(pageId) {
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(null);       // type='database' 페이지 레코드
  const [databaseId, setDatabaseId] = useState(null);
  const [columns, setColumns] = useState([]);
  const [views, setViews] = useState([]);
  const [rows, setRows] = useState([]);         // [{ page_id, properties, ...rowPage }]
  const [automations, setAutomations] = useState([]); // 트리거-액션 규칙 배열

  const load = useCallback(async () => {
    if (!pageId) return;
    setLoading(true);
    setError(null);
    try {
      // 1) 데이터베이스 페이지
      const { data: pg, error: pgErr } = await ws()
        .from('pages')
        .select('id, org_id, type, database_id, title, icon, position')
        .eq('id', pageId)
        .single();
      if (pgErr) throw pgErr;
      setPage(pg);

      // database_id: 컬럼이 있으면 사용, 없으면 페이지 id 자체를 db id 로 사용
      const dbId = pg.database_id || pg.id;
      setDatabaseId(dbId);

      // 2) 기본 스키마/뷰를 서버에서 멱등 보장(중복 생성·PK 충돌 방지) → 조회
      await supabase.rpc('ensure_database_defaults', { p_db: dbId });

      const { data: sch } = await ws()
        .from('db_schemas')
        .select('columns, automations')
        .eq('database_id', dbId)
        .maybeSingle();
      setColumns(
        sch && Array.isArray(sch.columns) && sch.columns.length ? sch.columns : DEFAULT_COLUMNS
      );
      setAutomations(sch && Array.isArray(sch.automations) ? sch.automations : []);

      const { data: vlist } = await ws()
        .from('db_views')
        .select('id, database_id, name, view_type, config, position')
        .eq('database_id', dbId)
        .order('position', { ascending: true });
      setViews(vlist || []);

      // 4) 행 — db_rows + 행 페이지(title/icon) 조인
      const { data: rrows, error: rErr } = await ws()
        .from('db_rows')
        .select('page_id, database_id, properties')
        .eq('database_id', dbId);
      if (rErr) throw rErr;

      const ids = (rrows || []).map((r) => r.page_id);
      let rowPages = {};
      if (ids.length) {
        // cover 는 갤러리 뷰 카드용. 컬럼이 없는 환경이면 cover 없이 재조회(graceful fallback).
        let rps = null;
        const res = await ws()
          .from('pages')
          .select('id, title, icon, cover, position')
          .in('id', ids);
        if (res.error) {
          const res2 = await ws()
            .from('pages')
            .select('id, title, icon, position')
            .in('id', ids);
          rps = res2.data;
        } else {
          rps = res.data;
        }
        (rps || []).forEach((rp) => { rowPages[rp.id] = rp; });
      }
      const merged = (rrows || [])
        .map((r) => ({
          page_id: r.page_id,
          properties: r.properties || {},
          title: rowPages[r.page_id]?.title || '',
          icon: rowPages[r.page_id]?.icon || '📄',
          cover: rowPages[r.page_id]?.cover || null,
          position: rowPages[r.page_id]?.position ?? 0,
        }))
        .sort((a, b) => a.position - b.position);
      setRows(merged);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [pageId]);

  useEffect(() => { load(); }, [load]);

  /* ---------------- 행 생성 ---------------- */
  const createRow = useCallback(
    async (initialProps = {}) => {
      if (!databaseId || !page) return null;
      const title = initialProps.title || '제목 없음';
      const props = { ...initialProps };
      delete props.title; // title 은 페이지 title 컬럼으로 저장

      // 1) db_row 페이지 생성
      const { data: rowPage, error: pErr } = await ws()
        .from('pages')
        .insert({
          org_id: page.org_id,
          type: 'db_row',
          database_id: databaseId,
          title,
          icon: '📄',
          created_by: user?.id ?? null,
          position: rows.length,
        })
        .select('id, title, icon, position')
        .single();
      if (pErr) { setError(pErr.message); return null; }

      // 2) db_rows 레코드 생성
      const fullProps = { title, ...props };
      const { error: rErr } = await ws()
        .from('db_rows')
        .insert({ page_id: rowPage.id, database_id: databaseId, properties: fullProps });
      if (rErr) { setError(rErr.message); return null; }

      const newRow = {
        page_id: rowPage.id,
        properties: fullProps,
        title: rowPage.title,
        icon: rowPage.icon || '📄',
        cover: null,
        position: rowPage.position ?? rows.length,
      };
      setRows((prev) => [...prev, newRow]);
      return newRow;
    },
    [databaseId, page, rows.length, user]
  );

  /* ---------------- 셀(속성) 수정 ---------------- */
  // 자동화 규칙 평가: 변경된 (key,value) 가 어떤 규칙의 트리거와 일치하면
  // 적용할 액션 패치 { [thenColumn]: value } 를 모아 반환한다.
  // 무한루프 방지: 1-pass 만 평가하고, 액션이 변경하는 컬럼(then.column)에 대해서는
  // 다시 규칙을 평가하지 않는다(아래 updateCell 에서 적용 후 재평가 없음).
  const computeAutomationPatch = useCallback(
    (changedKey, newValue) => {
      const patch = {};
      for (const rule of automations) {
        const when = rule?.when;
        const then = rule?.then;
        if (!when || !then || !when.column || !then.column) continue;
        // 트리거 컬럼이 방금 바뀐 컬럼이고, 새 값이 트리거 값과 같을 때
        if (when.column !== changedKey) continue;
        if (String(newValue) !== String(when.equals)) continue;
        // 자기 자신을 다시 바꾸는 규칙은 무시(즉시 루프 방지)
        if (then.column === changedKey) continue;
        let setVal = then.set;
        if (setVal === '@today') {
          const d = new Date();
          setVal = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        }
        patch[then.column] = setVal;
      }
      return patch;
    },
    [automations]
  );

  const updateCell = useCallback(
    async (pageRowId, key, value, _fromAutomation = false) => {
      const target = rows.find((r) => r.page_id === pageRowId);
      if (!target) return;

      // 트리거 컬럼이면 액션 패치를 함께 계산해 한 번에 반영.
      // _fromAutomation=true(자동화로 인한 변경)이면 재평가하지 않음(1-pass 가드).
      const autoPatch = _fromAutomation ? {} : computeAutomationPatch(key, value);
      const nextProps = { ...target.properties, [key]: value, ...autoPatch };

      // title 이 직접 변경됐거나 자동화로 변경된 경우 모두 반영해야 함
      const nextTitle = autoPatch.title !== undefined
        ? autoPatch.title
        : (key === 'title' ? value : undefined);

      // 낙관적 업데이트
      setRows((prev) =>
        prev.map((r) =>
          r.page_id === pageRowId
            ? { ...r, properties: nextProps, title: nextTitle !== undefined ? nextTitle : r.title }
            : r
        )
      );

      // db_rows.properties 갱신
      const { error: e1 } = await ws()
        .from('db_rows')
        .update({ properties: nextProps })
        .eq('page_id', pageRowId);
      if (e1) { setError(e1.message); return; }

      // title 이 (직접 또는 자동화로) 바뀌면 페이지 title 도 동기화
      if (nextTitle !== undefined) {
        const { error: e2 } = await ws()
          .from('pages')
          .update({ title: nextTitle })
          .eq('id', pageRowId);
        if (e2) setError(e2.message);
        else window.dispatchEvent(new Event('rt:pages'));
      }
    },
    [rows, computeAutomationPatch]
  );

  /* ---------------- 컬럼(스키마) 관리 ---------------- */
  // db_schemas.columns 를 통째로 저장하고 로컬 state 갱신하는 공통 헬퍼
  const persistColumns = useCallback(
    async (nextColumns) => {
      if (!databaseId) return false;
      const prev = columns;
      // 낙관적 업데이트
      setColumns(nextColumns);
      // db_schemas 행이 없을 수도 있으므로 upsert 사용.
      // automations 도 함께 보내 upsert 가 기존 규칙을 default([])로 덮어쓰지 않게 한다.
      const { error: e } = await ws()
        .from('db_schemas')
        .upsert(
          { database_id: databaseId, columns: nextColumns, automations },
          { onConflict: 'database_id' }
        );
      if (e) {
        setError(e.message);
        setColumns(prev); // 롤백
        return false;
      }
      return true;
    },
    [databaseId, columns, automations]
  );

  /* ---------------- 자동화 규칙 저장 ---------------- */
  // 규칙 배열을 통째로 저장. columns 도 함께 보내 upsert 가 컬럼을 덮어쓰지 않게 한다.
  const saveAutomations = useCallback(
    async (next) => {
      if (!databaseId) return false;
      const prev = automations;
      const arr = Array.isArray(next) ? next : [];
      setAutomations(arr); // 낙관적
      const { error: e } = await ws()
        .from('db_schemas')
        .upsert(
          { database_id: databaseId, columns, automations: arr },
          { onConflict: 'database_id' }
        );
      if (e) {
        setError(e.message);
        setAutomations(prev); // 롤백
        return false;
      }
      return true;
    },
    [databaseId, columns, automations]
  );

  // 새 컬럼 추가 (key 충돌 없게 생성)
  // meta: 타입별 부가 설정 — relation: { relationDb }, formula: { formula }, rollup: { rollup: {relationKey,targetKey,agg} }
  const addColumn = useCallback(
    async ({ name, type = 'text', meta }) => {
      const key = `col_${Date.now()}`;
      const col = { key, name: name || '새 속성', type, ...(meta && typeof meta === 'object' ? meta : {}) };
      if (type === 'select') col.options = [];
      const next = [...columns, col];
      const ok = await persistColumns(next);
      return ok ? col : null;
    },
    [columns, persistColumns]
  );

  // 컬럼 메타(수식/관계 대상 DB/롤업 설정 등) 부분 갱신
  const updateColumnMeta = useCallback(
    async (key, patch) => {
      const next = columns.map((c) => (c.key === key ? { ...c, ...patch } : c));
      return persistColumns(next);
    },
    [columns, persistColumns]
  );

  // 이름 변경 — formula 컬럼이 prop("표시이름") 으로 참조하므로,
  // 같은 스키마의 모든 formula 수식에서 prop("옛이름")/prop('옛이름') 을 새 이름으로 함께 치환한다.
  // (수식 문법상 속성 참조는 prop("...") / prop('...') 리터럴 형태만 존재 — formulaEngine.js)
  const renameColumn = useCallback(
    async (key, name) => {
      const oldName = columns.find((c) => c.key === key)?.name;
      const next = columns.map((c) => {
        if (c.key === key) return { ...c, name };
        if (
          c.type === 'formula' && typeof c.formula === 'string' &&
          oldName && oldName !== name
        ) {
          const rewritten = c.formula
            .split(`prop("${oldName}")`).join(`prop("${name}")`)
            .split(`prop('${oldName}')`).join(`prop("${name}")`);
          if (rewritten !== c.formula) return { ...c, formula: rewritten };
        }
        return c;
      });
      return persistColumns(next);
    },
    [columns, persistColumns]
  );

  // 타입 변경 (title 컬럼은 변경 불가)
  const changeColumnType = useCallback(
    async (key, type) => {
      const next = columns.map((c) => {
        if (c.key !== key || c.type === 'title') return c;
        const updated = { ...c, type };
        if (type === 'select') {
          if (!Array.isArray(updated.options)) updated.options = [];
        } else {
          delete updated.options;
        }
        // 타입별 메타는 해당 타입일 때만 유지 (다른 타입으로 바뀌면 정리)
        if (type !== 'formula') delete updated.formula;
        if (type !== 'rollup') delete updated.rollup;
        if (type !== 'relation') delete updated.relationDb;
        return updated;
      });
      return persistColumns(next);
    },
    [columns, persistColumns]
  );

  // 삭제 (title 컬럼은 삭제 불가). 행 properties 의 해당 key 값은 남겨둠(표시만 안 함).
  const deleteColumn = useCallback(
    async (key) => {
      const target = columns.find((c) => c.key === key);
      if (!target || target.type === 'title') return false;
      const next = columns.filter((c) => c.key !== key);
      return persistColumns(next);
    },
    [columns, persistColumns]
  );

  // select 옵션 갱신 (전체 옵션 배열 교체)
  const updateColumnOptions = useCallback(
    async (key, options) => {
      const next = columns.map((c) =>
        c.key === key ? { ...c, options: Array.isArray(options) ? options : [] } : c
      );
      return persistColumns(next);
    },
    [columns, persistColumns]
  );

  /* ---------------- 뷰 생성 ---------------- */
  // 새 뷰(표/보드/캘린더/폼 등) 추가. 추가된 뷰 레코드 반환.
  // 실패는 페이지 전역 error 로 올리지 않는다(페이지 전체가 에러 화면으로 바뀌는 것 방지)
  // — null 반환 + console.warn, 사용자 안내는 호출부(DatabasePage 뷰 추가 핸들러)가 담당.
  const createView = useCallback(
    async ({ view_type, name }) => {
      if (!databaseId) return null;
      const { data, error: e } = await ws()
        .from('db_views')
        .insert({
          database_id: databaseId,
          name: name || (view_type === 'form' ? '폼' : '새 뷰'),
          view_type,
          config: {},
          position: views.length,
        })
        .select('id, database_id, name, view_type, config, position')
        .single();
      if (e) {
        // eslint-disable-next-line no-console
        console.warn('[useDatabase] createView 실패:', e.message);
        return null;
      }
      setViews((prev) => [...prev, data]);
      return data;
    },
    [databaseId, views.length]
  );

  /* ---------------- 뷰 config 저장 ---------------- */
  // 특정 뷰(차트 등)의 config 를 통째로 갱신. db_views.config 에 저장.
  // 실패해도 페이지 전역 error 로 전환하지 않는다 — 콘솔 경고 + 조용한 롤백.
  // (필터/정렬 저장 실패로 데이터베이스 화면 전체가 에러 화면이 되는 것 방지)
  const saveViewConfig = useCallback(
    async (viewId, config) => {
      if (!viewId) return false;
      const prev = views;
      // 낙관적 업데이트
      setViews((list) => list.map((v) => (v.id === viewId ? { ...v, config } : v)));
      const { error: e } = await ws()
        .from('db_views')
        .update({ config })
        .eq('id', viewId);
      if (e) {
        // eslint-disable-next-line no-console
        console.warn('[useDatabase] saveViewConfig 실패(롤백):', e.message);
        setViews(prev); // 롤백
        return false;
      }
      return true;
    },
    [views]
  );

  return {
    loading,
    error,
    page,
    databaseId,
    columns,
    views,
    rows,
    automations,
    reload: load,
    createRow,
    updateCell,
    addColumn,
    updateColumnMeta,
    renameColumn,
    changeColumnType,
    deleteColumn,
    updateColumnOptions,
    saveAutomations,
    createView,
    saveViewConfig,
  };
}
